import { z } from 'zod';

/**
 * WhatsApp-supported media. Design doc §1 constraint 2: CSV is NOT supported —
 * convert to XLSX or TXT before calling this API.
 */
export const DOCUMENT_MIME_TYPES = {
  'application/pdf': ['.pdf'],
  'application/msword': ['.doc'],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
  'application/vnd.ms-excel': ['.xls'],
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
  'application/vnd.ms-powerpoint': ['.ppt'],
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': ['.pptx'],
  'text/plain': ['.txt'],
} as const satisfies Record<string, readonly string[]>;

export const IMAGE_MIME_TYPES = {
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/png': ['.png'],
} as const satisfies Record<string, readonly string[]>;

export const MAX_DOCUMENT_BYTES = 100 * 1024 * 1024; // 100 MB
export const MAX_IMAGE_BYTES = 5 * 1024 * 1024; // 5 MB

export type DocumentMimeType = keyof typeof DOCUMENT_MIME_TYPES;
export type ImageMimeType = keyof typeof IMAGE_MIME_TYPES;

export function isDocumentMime(mime: string): mime is DocumentMimeType {
  return mime in DOCUMENT_MIME_TYPES;
}

export function isImageMime(mime: string): mime is ImageMimeType {
  return mime in IMAGE_MIME_TYPES;
}

export function maxBytesFor(mime: string): number | null {
  if (isDocumentMime(mime)) return MAX_DOCUMENT_BYTES;
  if (isImageMime(mime)) return MAX_IMAGE_BYTES;
  return null;
}

/** The filename the customer sees must match the MIME type's extension. */
export function extensionMatchesMime(filename: string, mime: string): boolean {
  const dot = filename.lastIndexOf('.');
  if (dot < 0) return false;
  const ext = filename.slice(dot).toLowerCase();
  const allowed: readonly string[] | undefined = isDocumentMime(mime)
    ? DOCUMENT_MIME_TYPES[mime]
    : isImageMime(mime)
      ? IMAGE_MIME_TYPES[mime]
      : undefined;
  return allowed ? allowed.includes(ext) : false;
}

export const s3UriSchema = z
  .string()
  .trim()
  .regex(/^s3:\/\/[a-z0-9][a-z0-9.-]{1,61}[a-z0-9]\/.+$/i, 'Must be an s3://bucket/key URI');

/** Split `s3://bucket/some/key.pdf` into its parts. */
export function parseS3Uri(uri: string): { bucket: string; key: string } {
  const withoutScheme = uri.slice('s3://'.length);
  const slash = withoutScheme.indexOf('/');
  return { bucket: withoutScheme.slice(0, slash), key: withoutScheme.slice(slash + 1) };
}
